import React from "react";
import { useForm } from "react-hook-form";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { isValidEmail } from "lib/emailUtils";
import { EventNames, useTracking } from "lib/tracking";
import { BasicSignal, getSignalTypes } from "lib/types/models";
import { useShareNotice } from "../../lib/hooks/api/notices/useShareNotice";
import { Checkbox, Select, TextArea } from "../form_components/Inputs";

import css from "./ShareModal.module.scss";

type RecordDetails = {
  guid: string;
  signals?: BasicSignal[];
  stage?: string | null;
  score: number;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: RecordDetails;
};

const INPUT_TOKEN_SEPARATORS = [",", " ", "\n"];

function ShareModal({ open, onOpenChange, record }: Props): JSX.Element {
  const signalTypes = getSignalTypes(record.signals);
  const { control, handleSubmit } = useForm<{
    emails: string[];
    message: string;
    guids: string[];
    cc: boolean;
  }>({
    defaultValues: {
      emails: [],
      message: "",
      guids: [record.guid],
      cc: false,
    },
  });
  const { logEvent } = useTracking();

  const { mutate } = useShareNotice({
    onSuccess: (_, vars) => {
      toast.success("Email sent successfully!");
      logEvent(EventNames.shareEmailSent, {
        "Form values": vars,
        Records: {
          guid: record.guid,
          Stage: record.stage,
          "Signal types": signalTypes ? Object.keys(signalTypes) : [],
          "Signal score": record.score,
          Signals: signalTypes,
        },
        "Signal types": signalTypes ? Object.keys(signalTypes) : [],
        Signals: record.signals,
        Stages: [record.stage],
      });
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Failed to send email");
    },
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange} position="top-center">
      <Text h3>Share via email</Text>
      <form
        onSubmit={handleSubmit((values) =>
          mutate({
            input: {
              guids: values.guids,
              emails: values.emails,
              message: values.message || undefined,
              cc: values.cc,
            },
          }),
        )}
        className={css.form}
      >
        <Select
          name="emails"
          control={control}
          label="To"
          options={[]}
          tokenSeparators={INPUT_TOKEN_SEPARATORS}
          mode="tags"
          open={false}
          suffixIcon={null}
          rules={{
            minLength: 1,
            required: "Please enter at least one email address",
            validate: (v) => {
              // Check if it's an array
              if (!Array.isArray(v)) {
                return "Please enter at least one email address";
              }

              // Check if the array is not empty
              if (v.length === 0) {
                return "Please enter at least one email address";
              }

              // Check if every string in the array is a valid email
              const allValidEmails = v.every(
                (email) => typeof email === "string" && isValidEmail(email),
              );

              if (!allValidEmails) {
                return "Please enter a valid email address";
              }

              return allValidEmails;
            },
          }}
        />
        <TextArea name="message" control={control} label="Optional Message" rows={5} />
        <Checkbox name="cc" control={control} label="" fieldLabel="Also send to me" />
        <div className={css.footer}>
          <Button buttonType="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit">Send</Button>
        </div>
      </form>
    </Modal>
  );
}

export default ShareModal;
